import React from 'react';
import { useTable, useSortBy, usePagination, useFilters, useGlobalFilter, useAsyncDebounce } from 'react-table'
import { matchSorter } from 'match-sorter'
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, InputGroup, Input } from 'reactstrap';
import { Filters } from '../../services';
import Icon from '@mdi/react';
import { mdiSortAlphabeticalAscending, mdiSortAlphabeticalDescending } from '@mdi/js';


import  './Table.css';

// Create a default prop getter
const defaultPropGetter = () => ({})

const Table = ({ id, data, columns, getCellProps = defaultPropGetter, ...rest }) => {
    const { t } = useTranslation();

    const { color, bordered, sm, striped, hover, responsive } = rest;

    const classNames = ['table'];
    if (color)
        classNames.push(`table-${color}`);
    if (bordered)
        classNames.push('table-bordered');
    if (sm)
        classNames.push('table-sm');
    if (striped)
        classNames.push('table-striped');
    if (hover)
        classNames.push('table-hover');
    if (responsive)
        classNames.push('table-responsive');

    const filterTypes = React.useMemo(() => ({
        // Add a new fuzzyTextFilterFn filter type.
        fuzzyText: Filters.fuzzyTextFilterFn,
        // Or, override the default text filter to use
        // "startWith"
        text: (rows, id, filterValue) => {
            return rows.filter(row => {
                const rowValue = row.values[id]
                return rowValue !== undefined
                    ? String(rowValue)
                    .toLowerCase()
                    .startsWith(String(filterValue).toLowerCase())
                    : true
            })
        },
    }), []);

    const pagination = () => {
        return (
            <div className="pagination">
                <ButtonGroup className="leftButtons">
                    <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {'<<'}
                    </Button>
                    <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {'<'}
                    </Button>
                </ButtonGroup>

                <InputGroup className="centerGroup" style={{ width: 'auto' }}>
                    <ButtonGroup>
                        <Button className="currentPage" disabled>
                            <strong>
                                { t('common.page') }
                            </strong>
                        </Button>
                        <Input
                            type="number"
                            className="goToPage"
                            value={state.pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0
                                gotoPage(page)
                            }}
                        />
                        <Button className="currentPage" disabled>
                            <strong>
                                { t('common.on_y', {y: pageOptions.length}) }
                            </strong>
                        </Button>
                        <Input
                            name="select"
                            type="select"
                            className="form-control pageSize"
                            value={state.pageSize}
                            onChange={e => {
                                setPageSize(Number(e.target.value))
                            }}
                        >
                            {[10, 25, 50, 100].map(pageSize => (
                            <option key={pageSize} value={pageSize}>{pageSize}</option>
                            ))}
                        </Input>
                    </ButtonGroup>
                </InputGroup>

                <ButtonGroup className="rightButtons">
                    <Button onClick={() => nextPage()} disabled={!canNextPage}>
                        {'>'}
                    </Button>
                    <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        {'>>'}
                    </Button>
                </ButtonGroup>
            </div>
        );
    };

    const defaultColumn = React.useMemo(() => ({
        // Let's set up our default Filter UI
        Filter: ({ column: { filterable, filterValue, preFilteredRows, setFilter, ...rest }}) => {
            const count = preFilteredRows.length

            return (
                <Input
                    type="text"
                    value={filterValue || ''}
                    style={{ opacity: filterable ? 1 : 0 }}
                    onChange={e => {
                        setFilter(e.target.value || undefined)
                    }}
                />
            );
        }
    }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        // for pagination
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        // for filters
        visibleColumns,
        preGlobalFilteredRows,
        setGlobalFilter,
        // for pagination and filters
        state,
    } = useTable({
        columns,
        data,
        initialState: { pageIndex: 0, pageSize: 25 },
        defaultColumn,
        filterTypes,
    },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    )

    if (!data)
        return null;

    return (
        <div className="Table table-responsive">
            <table className={ classNames.join(' ') } {...getTableProps()} >
                <thead>
                    {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                        <th width={column.width ?? 'auto'} className="text-center" {...column.getHeaderProps(column.getSortByToggleProps())}>
                            {column.render('Header')}
                            <span>
                                {column.isSorted
                                ? column.isSortedDesc
                                ?  <>&nbsp;<Icon path={mdiSortAlphabeticalDescending} size={ 0.75 } /></>
                                :  <>&nbsp;<Icon path={mdiSortAlphabeticalAscending} size={ 0.75 } /></>
                                : ''}
                            </span>
                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                        </th>
                        ))}
                    </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                return <td className={cell.column.className} {...cell.getCellProps([getCellProps(cell)])}>{cell.render('Cell')}</td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            { pagination() }
        </div>
     )
}

export default Table;
